import React from "react";
import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { activeSocialLinks } from "../config/social";

// TikTok has no lucide-react icon — inline SVG matching the stroke
// style of the sibling icons so the row stays visually consistent.
const TikTokIcon = ({ size = 18, ...rest }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size}
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
       aria-hidden="true" {...rest}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: TikTokIcon,
  linkedin: Linkedin,
  youtube: Youtube,
};

/**
 * SocialLinks — renders the central MyAE social-media config as a row of
 * accessible icon links. Platforms with no URL in the config are skipped
 * (no greyed-out placeholder, no broken anchor).
 *
 * Props:
 *   className  — wrapper className
 *   iconSize   — override icon size (default 18)
 *   variant    — "muted" | "contrast" (default "muted") — colour on light footer
 */
export default function SocialLinks({ className = "", iconSize = 18, variant = "muted" }) {
  const links = activeSocialLinks();
  if (!links.length) return null;
  const base =
    variant === "contrast"
      ? "text-white/80 hover:text-white"
      : "text-zinc-500 hover:text-zinc-900";
  return (
    <ul
      className={`flex items-center gap-4 ${className}`}
      data-testid="social-links"
      aria-label="MyAE on social media"
    >
      {links.map((s) => {
        const Icon = ICONS[s.id];
        if (!Icon) return null;
        return (
          <li key={s.id}>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              title={s.label}
              data-testid={`social-link-${s.id}`}
              className={`inline-flex items-center justify-center rounded-full p-1.5 transition-colors ${base} focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400`}
            >
              <Icon size={iconSize} />
            </a>
          </li>
        );
      })}
    </ul>
  );
}
